import type { PipeStatusRow } from './usePipeStatusFilters';

export type PipeStatusTableRow = PipeStatusRow & {
  id: string | number;
  pipeData?: unknown;
  pipeEndIDA?: string | null;
  pipeEndIDB?: string | null;
  groupId?: string;
};

/** Treat null / empty / false as missing; "EndA", true, etc. as present. */
function isPresentEndValue(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'boolean') return value;
  const s = String(value).trim();
  if (!s) return false;
  const lower = s.toLowerCase();
  if (lower === 'false' || lower === 'null' || lower === '0' || lower === 'undefined') {
    return false;
  }
  return true;
}

/** Normalize API 0/1 / boolean / string flags to boolean (default false). */
export function isPipeFlagOn(value: unknown): boolean {
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    return s === '1' || s === 'true';
  }
  return false;
}

function asNonEmptyGroupId(value: unknown): string {
  if (value == null) return '';
  const s = String(value).trim();
  if (!s) return '';
  const lower = s.toLowerCase();
  if (lower === 'null' || lower === 'undefined' || lower === 'ungrouped') return '';
  return s;
}

function groupIdFromPipeDataItem(item: unknown): string {
  if (!item || typeof item !== 'object') return '';
  const obj = item as Record<string, unknown>;
  const nested =
    obj.pipeinfo && typeof obj.pipeinfo === 'object'
      ? (obj.pipeinfo as Record<string, unknown>)
      : null;
  return (
    asNonEmptyGroupId(obj.groupID) ||
    asNonEmptyGroupId(obj.groupId) ||
    asNonEmptyGroupId(obj.group_id) ||
    asNonEmptyGroupId(nested?.groupID) ||
    asNonEmptyGroupId(nested?.groupId) ||
    asNonEmptyGroupId(nested?.group_id)
  );
}

/**
 * Resolve group for the table:
 * 1) pipestatus.groupID / groupId when set
 * 2) else first non-empty group from pipeData ends (flat or nested pipeinfo)
 * If both ends have a group, the first available non-empty value wins.
 */
export function resolvePipeStatusGroupId(pipe: Record<string, unknown>): string {
  const fromStatus =
    asNonEmptyGroupId(pipe.groupID) || asNonEmptyGroupId(pipe.groupId);
  if (fromStatus) return fromStatus;

  const pipeData = pipe.pipeData;
  if (Array.isArray(pipeData)) {
    for (const item of pipeData) {
      const fromItem = groupIdFromPipeDataItem(item);
      if (fromItem) return fromItem;
    }
    return '';
  }
  if (pipeData && typeof pipeData === 'object') {
    return groupIdFromPipeDataItem(pipeData);
  }
  return '';
}

/** True when both End A and End B are present (same rules as the status table). */
export function pipeRowHasBothEnds(pipe: Record<string, unknown>): boolean {
  const row = mapPipeStatusToTableRow(pipe, 0);
  return Boolean(row.endA && row.endB);
}

/** Random Compat lot: both ends required; archived pipes excluded (flange still OK). */
export function pipeRowEligibleForRandomCompat(pipe: Record<string, unknown>): boolean {
  return pipeRowHasBothEnds(pipe) && !isPipeFlagOn(pipe.isArchive);
}

export function mapPipeStatusToTableRow(
  pipe: Record<string, unknown>,
  index: number,
  slNoOffset = 0
): PipeStatusTableRow {
  const pipeEndIDA = (pipe.pipeEndIDA as string | null | undefined) ?? null;
  const pipeEndIDB = (pipe.pipeEndIDB as string | null | undefined) ?? null;
  const groupId = resolvePipeStatusGroupId(pipe);

  // Match pipestatus columns only (API endA/endB mirror pipeEndIDA/pipeEndIDB).
  // Do not infer ends from pipeData — stale JSON can still list an end after the column is cleared.
  const endA = isPresentEndValue(pipe.endA) || isPresentEndValue(pipeEndIDA);
  const endB = isPresentEndValue(pipe.endB) || isPresentEndValue(pipeEndIDB);

  return {
    id: (pipe.id ?? pipe.pipeStatusId ?? pipe.pipeID) as string | number,
    slNo: slNoOffset + index + 1,
    projectNumber: String(pipe.projectNumber ?? ''),
    sequence: pipe.spread === 'Any' ? 'Unassigned' : String(pipe.spread ?? ''),
    status: String(pipe.status ?? ''),
    pipeID: String(pipe.pipeID ?? ''),
    endA,
    endB,
    isArchive: isPipeFlagOn(pipe.isArchive),
    isFlange: isPipeFlagOn(pipe.isFlange),
    isAnode: isPipeFlagOn(pipe.isAnode),
    pipeEndIDA,
    pipeEndIDB,
    groupId,
    pipeData: pipe.pipeData,
  };
}

export function mapPipeStatusToTableRows(
  pipes: Record<string, unknown>[],
  slNoOffset = 0
): PipeStatusTableRow[] {
  return pipes.map((pipe, index) => mapPipeStatusToTableRow(pipe, index, slNoOffset));
}
